<template>
  <div class="voice">
    <button type="button" class="voice__btn" :aria-label="playing ? 'Pause' : 'Play'" @click="toggle">
      <svg v-if="!playing" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
      <svg v-else viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
        <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
      </svg>
    </button>
    <div class="voice__track" @click="seek">
      <div class="voice__fill" :style="{ width: pct + '%' }" />
    </div>
    <span class="voice__time">{{ label }}</span>
    <audio ref="audioEl" :src="src" preload="metadata"
           @timeupdate="onTime" @loadedmetadata="onMeta" @ended="onEnded"
           @play="playing = true" @pause="playing = false" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const props = defineProps<{ src: string; durationHint?: number | null }>();

const audioEl = ref<HTMLAudioElement | null>(null);
const playing = ref(false);
const current = ref(0);
const total = ref(props.durationHint && props.durationHint > 0 ? props.durationHint : 0);

const pct = computed(() => (total.value > 0 ? Math.min(100, (current.value / total.value) * 100) : 0));
const label = computed(() => `${fmt(current.value)} / ${fmt(total.value)}`);

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function toggle() {
  const a = audioEl.value;
  if (!a) return;
  if (a.paused) void a.play(); else a.pause();
}

// MediaRecorder webm/opus clips often report duration=Infinity until seeked — the standard fix is to
// jump to the end once, which forces the browser to compute the real duration, then reset to 0.
function onMeta() {
  const a = audioEl.value;
  if (!a) return;
  if (!isFinite(a.duration) || isNaN(a.duration)) {
    try { a.currentTime = 1e101; } catch { /* ignore */ }
  } else {
    total.value = a.duration;
  }
}

function onTime() {
  const a = audioEl.value;
  if (!a) return;
  if (total.value === 0 || total.value === props.durationHint) {
    if (isFinite(a.duration) && a.duration > 0) { total.value = a.duration; a.currentTime = 0; current.value = 0; return; }
  }
  current.value = a.currentTime;
}

function onEnded() {
  playing.value = false;
  current.value = 0;
}

function seek(e: MouseEvent) {
  const a = audioEl.value;
  if (!a || total.value <= 0) return;
  const el = e.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  a.currentTime = ratio * total.value;
  current.value = a.currentTime;
}
</script>

<style scoped>
.voice {
  display: inline-flex; align-items: center; gap: 9px;
  min-width: 172px; max-width: 250px; padding: 2px 0;
}
.voice__btn {
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%;
  border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
  background: color-mix(in srgb, currentColor 16%, transparent); color: inherit;
  transition: background 0.15s ease;
}
.voice__btn:hover { background: color-mix(in srgb, currentColor 26%, transparent); }
.voice__track {
  flex: 1; height: 4px; border-radius: 2px; cursor: pointer; position: relative;
  background: color-mix(in srgb, currentColor 22%, transparent);
}
.voice__fill { height: 100%; border-radius: 2px; background: currentColor; }
.voice__time {
  flex: 0 0 auto; font-size: 11px; opacity: 0.72; font-variant-numeric: tabular-nums; letter-spacing: 0.02em;
}
</style>
